import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Upload, Download, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle, Copy } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Normalize a string for use in a domain: lowercase, strip diacritics,
// keep alphanumerics only. e.g. "St. Louis" -> "stlouis", "Hôpital" -> "hopital".
const slugifyForDomain = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");

export const ZipLookupPage = () => {
  const fileInputRef = useRef(null);
  const [csvFile, setCsvFile] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvStats, setCsvStats] = useState(null);

  const [pasteText, setPasteText] = useState("");
  const [pasteLoading, setPasteLoading] = useState(false);
  const [pasteResults, setPasteResults] = useState(null);

  // Country selector — drives backend dataset (US: GeoNames US.zip, ~41k
  // ZIPs, 30-mile radius; FR: GeoNames FR.zip, ~51k codes postaux,
  // 50-km radius). The backend exposes `country` on every result row.
  const [country, setCountry] = useState("US");
  const distanceUnit = country === "FR" ? "km" : "mi";
  const distanceLabel = country === "FR" ? "50 km" : "30 miles";
  const datasetSummary = country === "FR"
    ? "GeoNames FR (~51,000 codes postaux)"
    : "GeoNames US (~41,000 ZIPs)";

  // Bulk-clone export options
  const [bulkKeyword, setBulkKeyword] = useState("");
  const [bulkSuffix, setBulkSuffix] = useState("");

  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast.error("Please select a CSV file first");
      return;
    }
    setCsvLoading(true);
    setCsvStats(null);
    try {
      const formData = new FormData();
      formData.append("file", csvFile);
      formData.append("country", country);
      const res = await axios.post(`${API}/zip-lookup/enrich-csv`, formData, {
        responseType: "blob",
      });
      // Trigger download
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const baseName = csvFile.name.replace(/\.csv$/i, "");
      a.download = `${baseName}-enriched.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      const found = parseInt(res.headers["x-zip-found"] || "0", 10);
      const missing = parseInt(res.headers["x-zip-missing"] || "0", 10);
      const total = parseInt(res.headers["x-zip-total"] || "0", 10);
      setCsvStats({ found, missing, total });
      toast.success(`Enriched ${found.toLocaleString()} of ${total.toLocaleString()} ZIP codes`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.statusText || err.message;
      toast.error(`CSV enrichment failed: ${msg}`);
    } finally {
      setCsvLoading(false);
    }
  };

  const handlePasteLookup = async () => {
    const codes = pasteText
      .split(/[\s,;\n]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (codes.length === 0) {
      toast.error("Paste at least one ZIP code");
      return;
    }
    if (codes.length > 5000) {
      toast.error("Maximum 5,000 ZIP codes per request");
      return;
    }
    setPasteLoading(true);
    setPasteResults(null);
    try {
      const res = await axios.post(`${API}/zip-lookup`, {
        zip_codes: codes, country,
      });
      setPasteResults(res.data);
      toast.success(`Found ${res.data.found.toLocaleString()} of ${res.data.count.toLocaleString()}`);
    } catch (err) {
      toast.error(`Lookup failed: ${err.message}`);
    } finally {
      setPasteLoading(false);
    }
  };

  const downloadResultsCsv = () => {
    if (!pasteResults) return;
    const headers = ["zipcode", "city", "state", "state_code", "county", "population", "nearby_cities", "found"];
    const rows = pasteResults.results.map((r) => headers.map((h) => {
      let v = r[h];
      if (h === "nearby_cities" && Array.isArray(v)) {
        v = v.map((c) => `${c.city}, ${c.state_code} (${c.distance_miles}${distanceUnit})`).join("; ");
      }
      return `"${(v ?? "").toString().replace(/"/g, '""')}"`;
    }).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `zip-lookup-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  /**
   * Build the bulk-clone CSV expected by /api/projects/{id}/bulk-clone.
   * Columns: business_name, main_city, main_state, main_zip,
   *          surrounding_cities, domain_name
   *
   *   business_name = "{main_city} {main_keyword} {suffix}"  (suffix optional)
   *   surrounding_cities = "City:ST, City:ST, ..."           (5 nearby cities)
   *   domain_name = "{slug(main_city)}{slug(main_keyword)}.com"
   *
   * `minPopulation` filters out main cities (and nearby cities) below the
   * given population threshold — handy to focus the export on cities with
   * real local search volume.
   */
  const downloadBulkCloneCsv = (minPopulation = 0) => {
    if (!pasteResults) return;
    const keyword = bulkKeyword.trim();
    if (!keyword) {
      toast.error("Please enter a Main Keyword (e.g. Plumbing) before exporting");
      return;
    }
    const suffix = bulkSuffix.trim();
    const matched = pasteResults.results.filter((r) => r.found);
    if (matched.length === 0) {
      toast.error("No matched ZIP codes to export");
      return;
    }

    const eligible = matched.filter(
      (r) => (Number(r.population) || 0) >= minPopulation
    );
    if (eligible.length === 0) {
      toast.error(
        `No matched ZIP code has a population ≥ ${minPopulation.toLocaleString()}`
      );
      return;
    }

    // De-duplicate by (city, state_code): when several ZIPs map to the same
    // city (e.g. 60601, 60602, 60603 → Chicago), keep only the first one.
    const seenCities = new Set();
    const deduped = [];
    let duplicates = 0;
    for (const r of eligible) {
      const key = `${(r.city || "").toLowerCase()}|${r.state_code || ""}`;
      if (seenCities.has(key)) {
        duplicates += 1;
        continue;
      }
      seenCities.add(key);
      deduped.push(r);
    }
    const skipped = matched.length - eligible.length;

    const keywordSlug = slugifyForDomain(keyword);
    const headers = [
      "business_name", "main_city", "main_state", "main_zip",
      "surrounding_cities", "domain_name",
    ];

    const escape = (v) =>
      `"${(v ?? "").toString().replace(/"/g, '""')}"`;

    const rows = deduped.map((r) => {
      const businessName = [r.city, keyword, suffix].filter(Boolean).join(" ");
      const surrounding = (r.nearby_cities || [])
        .filter((c) => (Number(c.population) || 0) >= minPopulation)
        .map((c) => `${c.city}:${c.state_code}`)
        .join(", ");
      const domain = `${slugifyForDomain(r.city)}${keywordSlug}.com`;
      return [
        businessName, r.city, r.state_code, r.zipcode, surrounding, domain,
      ].map(escape).join(",");
    });

    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const filterTag = minPopulation > 0 ? `-min${minPopulation}pop` : "";
    a.download = `bulk-clone-${keywordSlug || "export"}${filterTag}-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    const parts = [];
    if (skipped > 0) parts.push(`${skipped} skipped (pop. < ${minPopulation.toLocaleString()})`);
    if (duplicates > 0) parts.push(`${duplicates} duplicate ${duplicates === 1 ? "city" : "cities"} merged`);
    const suffixMsg = parts.length > 0 ? ` · ${parts.join(" · ")}` : "";
    toast.success(`Bulk-clone CSV with ${deduped.length} project rows downloaded${suffixMsg}`);
  };

  return (
    <div className="p-8 max-w-5xl" data-testid="zip-lookup-page">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-orange-100 rounded-lg">
            <MapPin className="w-6 h-6 text-orange-600" />
          </div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>
            ZIP Code Lookup
          </h1>
        </div>
        <p className="text-slate-500">
          Enrich a list of {country === "FR" ? "French codes postaux" : "US ZIP codes"}
          {" "}with city, {country === "FR" ? "département" : "state"}, county,
          population and the 5 closest neighbouring cities (within {distanceLabel}).
          Powered by {datasetSummary}. Output CSV is bulk-clone ready.
        </p>
        {/* Country selector — flips the backend dataset between US ZIPs
            (GeoNames US.zip) and French codes postaux (GeoNames FR.zip). */}
        <div className="flex items-center gap-2 mt-4" data-testid="country-toggle">
          <Label className="text-sm font-medium text-slate-700">Country</Label>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
            {[
              { code: "US", label: "🇺🇸 United States" },
              { code: "FR", label: "🇫🇷 France" },
            ].map((opt) => (
              <button
                key={opt.code}
                type="button"
                onClick={() => {
                  setCountry(opt.code);
                  setPasteResults(null);
                  setCsvStats(null);
                }}
                data-testid={`country-${opt.code.toLowerCase()}-btn`}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  country === opt.code
                    ? "bg-orange-100 text-orange-700 font-medium"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card data-testid="zip-csv-upload-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" /> Upload CSV
            </CardTitle>
            <CardDescription>
              Upload a CSV with a <code>zipcode</code> column (or <code>zip</code>, <code>zip_code</code>,
              <code> postal_code</code>). All other columns are preserved. ZIP+4 (e.g. 60002-1234) is auto-trimmed.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv,text/csv"
              onChange={(e) => {
                setCsvFile(e.target.files?.[0] || null);
                setCsvStats(null);
              }}
              className="hidden"
              data-testid="zip-csv-file-input"
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-orange-400 hover:bg-orange-50/40 transition-colors"
              data-testid="zip-csv-drop-zone"
            >
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              {csvFile ? (
                <>
                  <p className="font-medium text-slate-700">{csvFile.name}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {(csvFile.size / 1024).toFixed(1)} KB · click to change
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium text-slate-700">Click to select a CSV file</p>
                  <p className="text-xs text-slate-500 mt-1">.csv only · up to ~10,000 rows</p>
                </>
              )}
            </div>

            <Button
              onClick={handleCsvUpload}
              disabled={!csvFile || csvLoading}
              className="w-full bg-orange-500 hover:bg-orange-600"
              data-testid="zip-csv-enrich-btn"
            >
              {csvLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enriching…</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Enrich & Download</>
              )}
            </Button>

            {csvStats && (
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid="zip-csv-stats">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span><strong>{csvStats.found.toLocaleString()}</strong> matched</span>
                  {csvStats.missing > 0 && (
                    <>
                      <span className="text-slate-400">·</span>
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                      <span><strong>{csvStats.missing.toLocaleString()}</strong> not found</span>
                    </>
                  )}
                  <span className="text-slate-400">·</span>
                  <span>{csvStats.total.toLocaleString()} total</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="zip-paste-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" /> Paste ZIP Codes
            </CardTitle>
            <CardDescription>
              Paste up to 5,000 ZIP codes (one per line, or comma/space-separated). Get an instant table preview, then download CSV if you like.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs">ZIP codes</Label>
              <Textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                rows={6}
                placeholder="60002&#10;60004&#10;90210&#10;98001"
                className="font-mono text-sm"
                data-testid="zip-paste-textarea"
              />
            </div>
            <Button
              onClick={handlePasteLookup}
              disabled={pasteLoading || !pasteText.trim()}
              className="w-full bg-orange-500 hover:bg-orange-600"
              data-testid="zip-paste-lookup-btn"
            >
              {pasteLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Looking up…</>
              ) : (
                <><MapPin className="w-4 h-4 mr-2" /> Lookup</>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {pasteResults && (
        <Card className="mt-6" data-testid="zip-paste-results-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Results</CardTitle>
                <CardDescription>
                  {pasteResults.found.toLocaleString()} found · {pasteResults.missing.toLocaleString()} not found · {pasteResults.count.toLocaleString()} total
                </CardDescription>
              </div>
              <Button
                onClick={downloadResultsCsv}
                variant="outline"
                size="sm"
                data-testid="zip-paste-download-btn"
              >
                <Download className="w-4 h-4 mr-2" /> Download CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Bulk-clone export builder */}
            <div
              className="mb-4 rounded-lg border border-orange-200 bg-orange-50/40 p-4"
              data-testid="bulk-clone-export-panel"
            >
              <div className="flex items-start gap-2 mb-3">
                <FileSpreadsheet className="w-5 h-5 text-orange-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-sm text-slate-800">
                    Export as bulk-clone CSV
                  </h3>
                  <p className="text-xs text-slate-600">
                    Generates one project per matched ZIP, ready for{" "}
                    <code className="text-[11px] bg-white px-1 py-0.5 rounded border">
                      POST /api/projects/&lt;id&gt;/bulk-clone
                    </code>
                    . Business name pattern:{" "}
                    <code className="text-[11px]">
                      &#123;city&#125; &#123;keyword&#125; &#123;suffix&#125;
                    </code>
                    {" — "}domain:{" "}
                    <code className="text-[11px]">
                      &#123;city&#125;&#123;keyword&#125;.com
                    </code>
                  </p>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3 mb-3">
                <div>
                  <Label className="text-xs" htmlFor="bulk-keyword-input">
                    Main keyword <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="bulk-keyword-input"
                    value={bulkKeyword}
                    onChange={(e) => setBulkKeyword(e.target.value)}
                    placeholder="Plumbing"
                    className="text-sm"
                    data-testid="bulk-clone-keyword-input"
                  />
                </div>
                <div>
                  <Label className="text-xs" htmlFor="bulk-suffix-input">
                    Suffix <span className="text-slate-400">(optional)</span>
                  </Label>
                  <Input
                    id="bulk-suffix-input"
                    value={bulkSuffix}
                    onChange={(e) => setBulkSuffix(e.target.value)}
                    placeholder="LLC, Pro, Experts…"
                    className="text-sm"
                    data-testid="bulk-clone-suffix-input"
                  />
                </div>
                <div className="flex items-end gap-2 sm:col-span-1">
                  <Button
                    onClick={() => downloadBulkCloneCsv(0)}
                    disabled={!bulkKeyword.trim() || pasteResults.found === 0}
                    className="flex-1 bg-orange-500 hover:bg-orange-600"
                    data-testid="bulk-clone-export-btn"
                  >
                    <Download className="w-4 h-4 mr-2" /> All cities
                  </Button>
                  <Button
                    onClick={() => downloadBulkCloneCsv(10000)}
                    disabled={!bulkKeyword.trim() || pasteResults.found === 0}
                    variant="outline"
                    className="flex-1 border-orange-300 hover:bg-orange-100"
                    title="Download only cities with population ≥ 10,000 (filters main + nearby cities)"
                    data-testid="bulk-clone-export-min10k-btn"
                  >
                    <Download className="w-4 h-4 mr-2" /> ≥ 10k pop.
                  </Button>
                </div>
              </div>
              {bulkKeyword.trim() && pasteResults.found > 0 && (
                <div
                  className="text-xs text-slate-700 bg-white rounded border border-orange-200 px-3 py-2 font-mono"
                  data-testid="bulk-clone-preview"
                >
                  <span className="text-slate-500">Preview row 1:</span>{" "}
                  <span className="font-semibold">
                    {[
                      pasteResults.results.find((r) => r.found)?.city,
                      bulkKeyword.trim(),
                      bulkSuffix.trim(),
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  </span>
                  {" → "}
                  <span className="text-orange-600">
                    {slugifyForDomain(
                      pasteResults.results.find((r) => r.found)?.city || ""
                    )}
                    {slugifyForDomain(bulkKeyword)}.com
                  </span>
                </div>
              )}
            </div>

            <div className="overflow-x-auto max-h-[480px] overflow-y-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-semibold">ZIP</th>
                    <th className="px-3 py-2 font-semibold">City</th>
                    <th className="px-3 py-2 font-semibold">State</th>
                    <th className="px-3 py-2 font-semibold">County</th>
                    <th className="px-3 py-2 font-semibold text-right">Population</th>
                    <th className="px-3 py-2 font-semibold">Nearby cities (≤ {distanceLabel})</th>
                  </tr>
                </thead>
                <tbody>
                  {pasteResults.results.map((r, i) => (
                    <tr key={i} className={r.found ? "border-t align-top" : "border-t bg-amber-50/50 align-top"}>
                      <td className="px-3 py-2 font-mono">{r.zipcode}</td>
                      <td className="px-3 py-2 font-medium">{r.city || <span className="text-slate-400 italic">—</span>}</td>
                      <td className="px-3 py-2">{r.state_code} {r.state && <span className="text-slate-500 text-xs">({r.state})</span>}</td>
                      <td className="px-3 py-2 text-slate-600">{r.county}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-slate-700">
                        {r.found && r.population > 0 ? r.population.toLocaleString() : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        {r.found && Array.isArray(r.nearby_cities) && r.nearby_cities.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {r.nearby_cities.map((c, j) => (
                              <span
                                key={j}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 border border-orange-200 text-xs"
                                title={`${c.distance_miles} ${distanceUnit} · pop. ${c.population.toLocaleString()} · ZIP ${c.zipcode}`}
                                data-testid={`nearby-city-chip-${i}-${j}`}
                              >
                                <span className="font-medium">{c.city}</span>
                                <span className="text-slate-500">{c.state_code}</span>
                                <span className="text-orange-600 font-mono">{c.distance_miles}{distanceUnit}</span>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs italic">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
